import { useQuery } from '@apollo/client';
import { FormField, Spinner, Text } from '@dixa/lib-shared-habla';
import { HookFormText } from 'lib-shared-hook-form';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { DeepWritable } from 'ts-essentials';

import { readOAuthProviderConfigurationSchemaQuery } from 'apis/partnerIntegrations/oAuth/partnerIntegrationsOAuth.queries';
import {
  OAuthProvider,
  OAuthStrategySchemaField,
} from 'apis/partnerIntegrations/oAuth/types/partnerIntegrationsOAuth.types';
import {
  OAuthStrategySchemaArg,
  OAuthStrategySchemaResponse,
} from 'apis/partnerIntegrations/oAuth/types/partnerIntegrationsOAuthApi.types';

import { OAuthProviderConfigurationSchemaField } from '../schemaField/oauthProviderConfigurationSchemaField';

type OAuthProviderSettingsConfigurationProps = {
  mode: 'create' | 'update';
  strategyId: string;
  initialValues?: OAuthProvider;
};

export type OAuthProviderConfigurationFormValues = Record<
  string,
  {
    key: string;
    secret: boolean;
    value: string;
    isEditing?: boolean;
  }
>;

/**
 * A component for rendering the settings configuration form of an OAuth
 * provider.
 *
 * @param mode - The mode of the form.
 * @param strategyId - The ID of the OAuth strategy.
 * @param initialValues - The initial values of the provider in update mode.
 * @returns The form fields for the OAuth provider settings configuration.
 */
export const OAuthProviderSettingsConfiguration = ({
  mode,
  strategyId,
  initialValues,
}: OAuthProviderSettingsConfigurationProps): JSX.Element => {
  const form = useFormContext();
  const [schema, setSchema] = useState<OAuthStrategySchemaField[]>([]);

  const isCreateMode = mode === 'create';
  const hasNoProviderToUpdate = !initialValues && mode === 'update';

  const { loading: isSchemaLoading, error: schemaError } = useQuery<
    OAuthStrategySchemaResponse,
    OAuthStrategySchemaArg
  >(readOAuthProviderConfigurationSchemaQuery, {
    variables: {
      id: strategyId,
    },
    onCompleted(data) {
      if (data.Management.oAuthProviderConfigurationSchema.length > 0) {
        const schema = data.Management.oAuthProviderConfigurationSchema;

        (schema as DeepWritable<OAuthStrategySchemaField[]>).sort(
          ({ required: a }, { required: b }) => +b - +a,
        );

        setSchema(schema as DeepWritable<OAuthStrategySchemaField[]>);

        const formValues = schema.reduce(
          (acc, item) => ({
            ...acc,
            [item.key]: {
              key: item.key,
              secret: item.secret,
              value:
                initialValues?.settings.find(
                  (setting) => setting.key === item.key,
                )?.value ||
                item.defaultValue ||
                '',
              // Show editing UI for create mode or non-secret fields in update mode
              isEditing: isCreateMode ? true : !item.secret,
            },
          }),
          {},
        );

        form.reset({
          ...formValues,
          name: {
            value: initialValues?.name || '',
            secret: false,
          },
        });
      } else {
        form.reset({});
      }
    },
  });

  if (hasNoProviderToUpdate) {
    return (
      <FormField>
        <Text.Paragraph>
          Something went wrong, there is no provider to update. Please contact
          support.
        </Text.Paragraph>
      </FormField>
    );
  }

  if (schemaError) {
    return (
      <FormField>
        <Text.Paragraph>
          Failed to load OAuth provider configuration schema. Please try again
          later or contact support.
        </Text.Paragraph>
      </FormField>
    );
  }

  return isSchemaLoading ? (
    <Spinner />
  ) : (
    <>
      <FormField>
        <HookFormText
          label="Name"
          name="name.value"
          rules={{
            required: 'The name is required',
          }}
          componentProps={{
            placeholder: 'Enter a name for your OAuth provider',
          }}
        />
      </FormField>

      {schema.map((field) => (
        <FormField key={field.key}>
          <OAuthProviderConfigurationSchemaField
            isCreateMode={isCreateMode}
            field={field}
          />
        </FormField>
      ))}
    </>
  );
};
